import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./../general/AuthContext";
import PageHead from "./../general/PageHead";
import { ChevronDown, LogIn, Edit, Plus, Trash2, LogOut, FileUp, FileMinus, Users, Download, RefreshCw, Search } from 'lucide-react';

const nameMapping = {
  RepAssigned1: "Staff Rep 1", RepAssigned2: "Staff Rep 2",
  RepAssigned1_Id: "Representative 1 ID", RepAssigned2_Id: "Representative 2 ID",
  ServiceFieldRep1: "Service Rep 1", ServiceFieldRep1_Id: "Service Rep 1 ID",
  ServiceFieldRep2: "Service Rep 2", ServiceFieldRep2_Id: "Service Rep 2 ID",
  FieldRep1: "Field Rep", FieldRep1_Id: "Field Rep ID",
  FieldRep2: "Field Rep 2", FieldRep2_Id: "Field Rep 2 ID",
  Organizer: "Organizer", Organizer_Id: "Organizer ID",
  TOWN: "Town", CBA_START: "CBA Start Date", CBA_EXP: "CBA Expiry Date",
  PayrollHRContactName: "Payroll HR Contact", PayrollHRContactEmail: "Payroll HR Contact Email",
  PayrollHRContactNumber: "Payroll HR Contact Number", PayrollHRContactTitle: "Payroll HR Contact Title",
  PayrollHRContactType: "Payroll HR Contact Type",
  ActiveJobCount: "Active Job Count", TotalDuesPayers: "Total Dues Payers",
  OnCentralizedDues: "On Centralized Dues",
  NumberOfHomeEmails: "Home Emails Count", NumberOfPhoneNumbers: "Phone Numbers Count",
  DateLastBargUnitListReceived: "Last BU List Date",
  AuditCompleteOverview: "Audit Completion Date",
  PEOPLEContributors: "PEOPLE Contributors", TotalOPTOUTS: "Total OPT-OUTS",
  NoCardNoDues: "No Card No Dues", DuesNoCard: "Dues With No Card",
  CardNoDues: "Card No Dues", DuesWithCard: "Dues With Card",
  RecommitsSaves: "Recommits Saves", DateLastOfficerUpdate: "Last Officer Update Date",
  LastElection: "Last Election Date", IUMonthYearNextElection: "Next Election Date",
  LengthOfTerm: "Length of Term", PresidentContactNumber: "President's Contact Number",
  bu_STAFF_ASSIGNED: "Staff Assigned", bu_STAFF_ASSIGNED_ID: "Staff Assigned ID",
  PrimaryKey: "Bargaining Unit ID",
  VicePresidentFirstName: "VP First Name", VicePresidentLastName: "VP Last Name",
  PresidentFirstName: "President First Name", PresidentLastName: "President Last Name",
  TreasurerFirstName: "Treasurer First Name", TreasurerLastName: "Treasurer Last Name",
  RecordingSecretaryFirstName: "Secretary First Name", RecordingSecretaryLastName: "Secretary Last Name",
};

function opLabel(type) {
  const map = {
    LOGIN: "User Login", LOGIN_SUCCESS: "Login Success", LOGIN_FAILED: "Failed Login",
    LOGIN_TOKEN_REQUESTED: "Login Link Requested", LOGIN_TOKEN_INVALID: "Invalid Login Link",
    LOGIN_TOKEN_REUSE: "Login Link Reused", LOGIN_TOKEN_EXPIRED: "Login Link Expired",
    LOGIN_CODE_SUCCESS: "Code Login", LOGIN_CODE_INVALID: "Invalid Code",
    LOGIN_CODE_REUSE: "Code Used", LOGIN_CODE_EXPIRED: "Code Expired",
    LOGOUT: "User Logout", USER_INVITED: "User Invited", ASSIGN_EMPLOYEES: "Assigned Employees",
    DOCUMENT_UPLOAD: "Document Uploaded", DOCUMENT_DELETE: "Document Deleted",
    CREATE: "Created", DELETE: "Deleted",
  };
  return map[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : '');
}

function feedIconClass(type) {
  if (!type) return '';
  const t = type.toUpperCase();
  if (t === 'UPDATE') return 'update';
  if (['INSERT', 'CREATE', 'DOCUMENT_UPLOAD', 'ASSIGN_EMPLOYEES'].includes(t)) return 'create';
  if (['DELETE', 'DOCUMENT_DELETE', 'LOGIN_FAILED', 'LOGIN_TOKEN_INVALID', 'LOGIN_TOKEN_REUSE', 'LOGIN_TOKEN_EXPIRED'].includes(t)) return 'delete';
  if (['LOGIN', 'LOGIN_SUCCESS', 'LOGIN_CODE_SUCCESS', 'LOGIN_TOKEN_REQUESTED'].includes(t)) return 'login';
  if (t === 'LOGOUT') return 'logout';
  return '';
}

function FeedIcon({ type }) {
  const t = (type || '').toUpperCase();
  const cls = feedIconClass(t);
  const Icon = t === 'UPDATE' ? Edit
    : ['INSERT','CREATE','DOCUMENT_UPLOAD','ASSIGN_EMPLOYEES','USER_INVITED'].includes(t) ? Plus
    : ['DELETE','DOCUMENT_DELETE'].includes(t) ? Trash2
    : ['LOGOUT'].includes(t) ? LogOut
    : t === 'DOCUMENT_UPLOAD' ? FileUp
    : t === 'DOCUMENT_DELETE' ? FileMinus
    : t === 'ASSIGN_EMPLOYEES' ? Users
    : LogIn;
  return <div className={`feed-icon ${cls}`}><Icon size={16} /></div>;
}

const AuditLog = () => {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  const [logs, setLogs] = useState([]);
  const [displayItems, setDisplayItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unitDetails, setUnitDetails] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalRecords] = useState(0);
  const [itemsPerPage] = useState(50);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!auth.isAuthenticated) { navigate("/login"); return; }
  }, [auth, navigate]);

  useEffect(() => {
    if (auth.isAuthenticated) fetchLogs();
  }, [currentPage]); // eslint-disable-line

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: itemsPerPage });
      const response = await fetch(`/api/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      let rawLogs, pagination;
      if (Array.isArray(data)) {
        rawLogs = data;
        pagination = { currentPage: 1, totalPages: 1, totalRecords: data.length, recordsPerPage: data.length };
      } else if (data.data && Array.isArray(data.data)) {
        rawLogs = data.data;
        pagination = data.pagination || { currentPage: 1, totalPages: 1, totalRecords: data.data.length, recordsPerPage: data.data.length };
      } else {
        throw new Error('Unexpected response format');
      }
      const processedLogs = rawLogs.map(log => ({
        ...log,
        Differences: log.Differences ? JSON.parse(log.Differences) : {},
        ChangedData: log.ChangedData ? JSON.parse(log.ChangedData) : {},
      }));
      setLogs(processedLogs);
      setDisplayItems(groupLoginLogoutPerDay(processedLogs));
      setTotalPages(pagination.totalPages);
      setTotalRecords(pagination.totalRecords);
      setCurrentPage(pagination.currentPage);

      const unitRelatedOps = ['UPDATE', 'INSERT', 'DELETE', 'ASSIGN_EMPLOYEES', 'DOCUMENT_UPLOAD', 'DOCUMENT_DELETE'];
      const uniqueUnitIds = [...new Set(
        processedLogs.filter(l => unitRelatedOps.includes(l.OperationType) && l.BargainingUnitId).map(l => l.BargainingUnitId).filter(Boolean)
      )];
      const unitDetailsMap = {};
      await Promise.all(uniqueUnitIds.map(async id => {
        try {
          const r = await fetch(`/api/unit-info/${id}`, { headers: { Authorization: `Bearer ${auth.token}` } });
          if (r.ok) {
            const u = await r.json();
            unitDetailsMap[id] = { name: u.BARGAINING_UNIT || u.bu_NAME || '', local: u.LOCAL || u.bu_LOCAL || '', town: u.TOWN || u.bu_TOWN || '' };
          }
        } catch {}
      }));
      setUnitDetails(unitDetailsMap);
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      setError("Failed to fetch data. Please try again later.");
      if (err.message.includes('401')) { setAuth({ isAuthenticated: false, token: null }); navigate("/login"); }
    } finally {
      setLoading(false);
    }
  };

  const isLoginOrLogout = (op) => {
    const t = String(op || '').toUpperCase();
    return ['LOGIN','LOGIN_SUCCESS','LOGIN_FAILED','LOGIN_TOKEN_REQUESTED','LOGIN_TOKEN_INVALID',
      'LOGIN_TOKEN_REUSE','LOGIN_TOKEN_EXPIRED','LOGIN_CODE_SUCCESS','LOGIN_CODE_INVALID',
      'LOGIN_CODE_REUSE','LOGIN_CODE_EXPIRED','LOGOUT'].includes(t);
  };

  const groupLoginLogoutPerDay = (inputLogs) => {
    const items = [];
    const groupIndex = new Map();
    const getDayBucket = ts => { const d = new Date(ts); d.setHours(0,0,0,0); return d.getTime(); };
    inputLogs.forEach(log => {
      if (!isLoginOrLogout(log.OperationType)) { items.push({ kind: 'log', log }); return; }
      const key = `${log.ChangedBy}|${getDayBucket(log.ChangeTimestamp)}`;
      const idx = groupIndex.get(key);
      if (idx !== undefined) {
        items[idx].items.push(log); items[idx].count += 1;
      } else {
        const g = { kind: 'login_group', id: `lg-${log.ChangedBy}-${getDayBucket(log.ChangeTimestamp)}`, user: log.ChangedBy, items: [log], count: 1, bucketStart: getDayBucket(log.ChangeTimestamp) };
        items.push(g);
        groupIndex.set(key, items.length - 1);
      }
    });
    return items;
  };

  const formatDayLabel = ts => { try { return new Date(ts).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch { return ''; } };
  const formatTimeOfDay = ts => { try { return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  const toggleGroup = id => setExpandedGroups(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const formatTimestamp = ts => {
    const d = new Date(ts), now = new Date(), h = Math.floor((now-d)/3600000);
    if (h < 1) return `${Math.floor((now-d)/60000)}m ago`;
    if (h < 24) return `${h}h ago`;
    if (h < 48) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatChangeDetails = (differences, operationType, changedData) => {
    if (operationType === "DOCUMENT_UPLOAD") return (
      <div className="feed-changes"><span className="feed-change">Document: <b>{changedData?.file_name || 'file'}</b></span></div>
    );
    if (operationType === "DOCUMENT_DELETE") return (
      <div className="feed-changes"><span className="feed-change">Deleted: <b>{changedData?.file_name || 'file'}</b></span></div>
    );
    if (isLoginOrLogout(operationType)) return (
      <div className="feed-changes"><span className="feed-change">{differences.action || operationType}</span></div>
    );
    if (operationType === "CREATE" && differences.note) return (
      <div className="feed-changes"><span className="feed-change">Note: <b>{differences.note}</b></span></div>
    );
    if (operationType === "ASSIGN_EMPLOYEES" && differences.assignments) {
      return (
        <div className="feed-changes">
          {Object.entries(differences.assignments).filter(([,v]) => v).map(([k,v]) => (
            <span key={k} className="feed-change">{k}: <b>{v}</b></span>
          ))}
        </div>
      );
    }
    const entries = Object.entries(differences).filter(([k]) => k !== "id" && !k.endsWith("_Id") && k !== "action");
    if (!entries.length) return null;
    return (
      <div className="feed-changes">
        {entries.map(([k, obj]) => (
          <span key={k} className="feed-change">
            {nameMapping[k] || k}: <span className="dh-muted">{obj.old || '—'}</span> → <b>{obj.new || '—'}</b>
          </span>
        ))}
      </div>
    );
  };

  const handlePageChange = n => { setCurrentPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const matchesFilter = (item) => {
    if (filter === "all") return true;
    if (item.kind === 'login_group') {
      return filter === "login";
    }
    const op = (item.log?.OperationType || '').toUpperCase();
    if (filter === "update") return op === 'UPDATE';
    if (filter === "create") return ['INSERT','CREATE','DOCUMENT_UPLOAD','ASSIGN_EMPLOYEES','USER_INVITED'].includes(op);
    if (filter === "login") return isLoginOrLogout(op);
    return true;
  };

  const matchesSearch = (item) => {
    if (!q.trim()) return true;
    const lq = q.toLowerCase();
    if (item.kind === 'login_group') {
      return (item.user || '').toLowerCase().includes(lq);
    }
    const log = item.log;
    const unit = log.BargainingUnitId ? unitDetails[log.BargainingUnitId] : null;
    return (
      (log.ChangedBy || '').toLowerCase().includes(lq) ||
      (unit?.name || '').toLowerCase().includes(lq) ||
      (unit?.local || '').toString().toLowerCase().includes(lq) ||
      (unit?.town || '').toLowerCase().includes(lq) ||
      (log.OperationType || '').toLowerCase().includes(lq)
    );
  };

  const filteredItems = displayItems.filter(item => matchesFilter(item) && matchesSearch(item));

  const handleExport = () => {
    const rows = [['Timestamp','User','Operation','Unit','Details']];
    logs.forEach(log => {
      const unit = log.BargainingUnitId ? unitDetails[log.BargainingUnitId] : null;
      const details = Object.entries(log.Differences || {})
        .filter(([k]) => k !== 'id' && !k.endsWith('_Id') && k !== 'action')
        .map(([k, v]) => `${nameMapping[k] || k}: ${v?.old ?? ''} → ${v?.new ?? ''}`)
        .join('; ');
      rows.push([
        new Date(log.ChangeTimestamp).toISOString(),
        log.ChangedBy || '',
        log.OperationType || '',
        unit ? `${unit.name} (Local ${unit.local})` : (log.BargainingUnitId || ''),
        details,
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHead
        title="Activity Feed"
        sub="Track all system actions, changes, and user activity"
        actions={
          <>
            <button className="btn btn-secondary" onClick={handleExport}>
              <Download size={16} /> Export
            </button>
            <button className="btn btn-secondary" onClick={() => fetchLogs()}>
              <RefreshCw size={16} /> Refresh
            </button>
          </>
        }
      />

      <div className="toolbar list-toolbar">
        <div className="pills">
          {[["all","All"],["update","Updates"],["create","Creates"],["login","Logins"]].map(([k,l]) => (
            <button key={k} className={"pill" + (filter === k ? " active" : "")} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <div className="dh-row-end list-toolbar-right" style={{ gap: 12 }}>
          <div className="search-box">
            <Search className="ic" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by user, unit…" />
          </div>
          <span className="dh-tiny dh-muted">{filteredItems.length} entries</span>
        </div>
      </div>

      {error ? (
        <div className="card card-pad" style={{ color: 'var(--color-danger)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="empty-state"><p style={{ color: 'var(--fg-2)' }}>Loading activity…</p></div>
          ) : filteredItems.length === 0 ? (
            <div className="empty-state"><p>No matching entries found.</p></div>
          ) : (
            filteredItems.map((item, idx) => {
              if (item.kind === 'log') {
                const log = item.log;
                const unit = log.BargainingUnitId ? unitDetails[log.BargainingUnitId] : null;
                return (
                  <div key={log.LogID} className="feed-item">
                    <FeedIcon type={log.OperationType} />
                    <div className="feed-body">
                      <div className="feed-headline">
                        <b>{log.ChangedBy}</b>{' '}
                        {log.OperationType === 'CREATE' && log.TableName === 'Notes' ? 'added a note' :
                         log.OperationType === 'DELETE' && log.TableName === 'Notes' ? 'deleted a note' :
                         log.OperationType === 'DOCUMENT_UPLOAD' ? 'uploaded a document' :
                         log.OperationType === 'DOCUMENT_DELETE' ? 'deleted a document' :
                         log.OperationType === 'ASSIGN_EMPLOYEES' ? 'assigned employees' :
                         unit ? <>updated <span className="feed-target">{unit.name || `Unit ${log.BargainingUnitId}`}</span></> :
                         opLabel(log.OperationType).toLowerCase()}
                      </div>
                      {unit && (
                        <div className="feed-meta">Local {unit.local} · {unit.town}</div>
                      )}
                      {formatChangeDetails(log.Differences, log.OperationType, log.ChangedData)}
                      {log.BargainingUnitId && (
                        <div style={{ marginTop: 6 }}>
                          <Link to={`/unit-info/${log.BargainingUnitId}`} style={{ fontSize: 12.5, color: 'var(--fg-link)' }}>
                            View unit →
                          </Link>
                        </div>
                      )}
                    </div>
                    <div className="feed-time">
                      {formatTimestamp(log.ChangeTimestamp)}
                    </div>
                  </div>
                );
              }

              // login_group
              const isOpen = expandedGroups.has(item.id);
              return (
                <div key={item.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 22px', cursor: 'pointer' }}
                    onClick={() => toggleGroup(item.id)}
                  >
                    <div className="feed-icon login"><LogIn size={16} /></div>
                    <div className="feed-body">
                      <div className="feed-headline" style={{ fontSize: 13.5 }}>
                        <b>{item.user}</b> — Login/Logout activity
                      </div>
                      <div className="feed-meta">{item.count} events · {formatDayLabel(item.bucketStart)}</div>
                    </div>
                    <ChevronDown
                      size={16}
                      style={{ color: 'var(--fg-3)', transition: 'transform 200ms', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
                    />
                  </div>
                  {isOpen && (
                    <div style={{ paddingLeft: 60, paddingBottom: 12 }}>
                      {item.items.map(log => (
                        <div key={log.LogID} style={{ padding: '6px 22px 6px 0', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 12, alignItems: 'start' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-3)', paddingTop: 2, minWidth: 60 }}>
                            {formatTimeOfDay(log.ChangeTimestamp)}
                          </span>
                          <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>{opLabel(log.OperationType)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Pagination footer */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--fg-2)' }}>
            <span>Showing {filteredItems.length} entries</span>
            <div className="dh-row-end" style={{ gap: 6 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              {totalPages > 1 && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)', padding: '0 8px' }}>
                  {currentPage} / {totalPages}
                </span>
              )}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditLog;
